import { Divider, Icon, LoadingDots } from '@advanza/ui'
import { Note, NOTE_TYPE_AI } from '@trustoo/types/entity/note'
import ReclaimPercentageTable from 'components/reclaims/ReclaimPercentageTable'
import { useReclaimsList, useReclaimStatusCounts } from 'hooks/reclaimHooks'
import { Reclaim } from 'interfaces/Reclaims'
import { useEffect, useMemo } from 'react'
import Chart from 'react-google-charts'
import style from './reclaimTaskDetails.module.css'

interface ReclaimTaskDetailProps {
    reclaim: Reclaim
    providerId: number
    notes: Note[]
}

const ReclaimTaskDetails = ({ reclaim, providerId, notes }: ReclaimTaskDetailProps) => {
    const noteIds = reclaim.notes
    const lastNoteId = noteIds?.[noteIds.length - 1]
    const lastNote = lastNoteId ? notes?.[lastNoteId] : undefined

    const { isFetchingReclaimStatusCounts, reclaimStatusCounts } =
        useReclaimStatusCounts(providerId)

    const statusChartData = useMemo(() => {
        if (!reclaimStatusCounts || Object.keys(reclaimStatusCounts).length === 0) return null

        return [
            ['Status', 'Count'],
            ['accepted', reclaimStatusCounts.accepted ?? 0],
            ['denied', reclaimStatusCounts.denied ?? 0],
        ]
    }, [reclaimStatusCounts])

    const {
        stats: { nrReclaimsPerReason = [] } = {},
        refresh,
        isFetching: isFetchingReasonsCount,
    } = useReclaimsList(`p_${providerId}`, {
        providerId: providerId,
    })

    const reasonChartData = [
        ['Reason', 'Count'],
        ...(
            nrReclaimsPerReason?.map(({ reason, amount }: { reason: string; amount: number }) => [
                reason || 'No reason',
                Number(amount),
            ]) ?? []
        ).sort((a: number[], b: number[]) => b[1] - a[1]),
    ]

    useEffect(() => {
        if (providerId) refresh()
    }, [providerId])
    return (
        <>
            <div className="grid grid-cols-2 gap-4">
                <div>
                    <div className={style.infoContainer}>
                        {reclaim.reason ?? 'Reason not specified'}
                    </div>
                    <Divider sm />
                    <div className={style.infoContainer}>
                        <ReclaimPercentageTable reclaim={reclaim} />
                    </div>
                </div>
                <div>
                    <div className={style.infoContainer}>
                        {lastNote ? (
                            <>
                                {lastNote.note_type === NOTE_TYPE_AI && (
                                    <>
                                        <b>AI</b>{' '}
                                        <Icon
                                            classNameIcon="!align-bottom"
                                            name="psychology"
                                        />{' '}
                                    </>
                                )}
                                {lastNote.note}
                            </>
                        ) : (
                            'No Notes'
                        )}
                    </div>
                </div>
            </div>
            <Divider sm />
            <div className="grid grid-cols-2 gap-4">
                <div>
                    {isFetchingReclaimStatusCounts ? (
                        <LoadingDots />
                    ) : (
                        <Chart
                            width={'100%'}
                            height={'240px'}
                            chartType="PieChart"
                            loader={<div>Loading Chart</div>}
                            data={statusChartData ?? []}
                            options={{
                                title: 'Reclaim Decision Breakdown',
                                colors: ['#0ea66b', '#f42435'],
                                titleTextStyle: { fontSize: 14 },
                                chartArea: {
                                    width: '100%',
                                    height: '80%',
                                    top: 50,
                                },
                                legend: {
                                    textStyle: {
                                        fontSize: 12,
                                    },
                                },
                            }}
                        />
                    )}
                </div>
                <div>
                    {isFetchingReasonsCount ? (
                        <LoadingDots />
                    ) : (
                        <Chart
                            width="100%"
                            height="240px"
                            chartType="PieChart"
                            loader={<div>Loading Chart</div>}
                            data={reasonChartData}
                            options={{
                                title: 'Reclaims By Reason',
                                titleTextStyle: { fontSize: 14 },
                                chartArea: {
                                    width: '100%',
                                    height: '80%',
                                    top: 50,
                                },
                                legend: {
                                    textStyle: {
                                        fontSize: 12,
                                    },
                                },
                            }}
                        />
                    )}
                </div>
            </div>
        </>
    )
}

export default ReclaimTaskDetails
